import z from "zod";
import {
  BANK_ACCOUNT_TYPE,
  DISTRIBUTOR_BANK_ACCOUNT_TYPE_OPTIONS,
  NOMINEE_RELATIONSHIP,
} from "../constants";

/** Matches login Username field sanitization (letters, digits, `.`, `_`, `-`). */
export const ARN_USERNAME_REGEX = /^[a-zA-Z0-9._-]+$/;

export const arnSchema = z
  .string()
  .min(1, "ARN code is required")
  .regex(
    ARN_USERNAME_REGEX,
    "ARN can only contain letters, numbers, and . _ - characters"
  );

export const accountNumberSchema = z
  .string()
  .regex(/^\d{9,18}$/, "Account number must be 9 to 18 digits");

export const ifscCodeSchema = z
  .string()
  .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC code format");

export const accountTypeSchema = z.enum([
  ...(BANK_ACCOUNT_TYPE.map((d) => d.value) as [string, ...string[]]),
  "",
]);

export const accountTypeOptionsSchema = z.enum(
  DISTRIBUTOR_BANK_ACCOUNT_TYPE_OPTIONS.map((d) => d.value) as [
    string,
    ...string[],
  ]
);

const nomineeRelationshipValues = NOMINEE_RELATIONSHIP.map((d) => d.value);

export const nomineeRelationshipSchema = z.enum(
  nomineeRelationshipValues as [string, ...string[]]
);

export const phoneNumberSchema = z
  .string()
  .regex(/^\+91\d{10}$/, "Phone number must be 10 digits");

export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export const panSchema = z
  .string()
  .min(1, "PAN is required")
  .regex(PAN_REGEX, "Invalid PAN format");

export const nameSchema = z
  .string()
  .min(2, "Name must be at least 2 characters")
  .max(50, "Name cannot exceed 50 characters")
  .regex(
    /^[a-zA-Z\s.]+$/,
    "Name cannot contain numbers and special characters"
  );

export const GST_REGEX =
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export const gstSchema = z.string().regex(GST_REGEX, "Invalid GST number");

export const dobSchema = z
  .string()
  .refine(
    (d) => {
      if (!d) return true;
      return !Number.isNaN(new Date(d).getTime());
    },
    {
      message: "Date of birth is invalid",
    }
  )
  .refine(
    (d) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const date = new Date(d);
      date.setHours(0, 0, 0, 0);
      return date <= today;
    },
    {
      message: "Date of birth cannot be in the future",
    }
  );

export const nonMinor = z
  .string()
  .refine(
    (d) => {
      if (!d) return true;
      return !Number.isNaN(new Date(d).getTime());
    },
    {
      message: "Date of birth is invalid",
    }
  )
  .refine(
    (d) => {
      if (!d) return true;
      const dob = new Date(d);
      if (Number.isNaN(dob.getTime())) return true;

      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const hasHadBirthdayThisYear =
        today.getMonth() > dob.getMonth() ||
        (today.getMonth() === dob.getMonth() &&
          today.getDate() >= dob.getDate());
      if (!hasHadBirthdayThisYear) age -= 1;

      return age >= 18;
    },
    {
      message: "Must be older than 18 years",
    }
  );
